import type { CmtsAdminResourceConfig } from "./cmtsAdminTypes.js";

const activeColumn = {
  id: "status",
  header: "Status",
  sortable: true,
  filterable: true,
  minWidth: 120,
  render: (row: Record<string, unknown>) => <span className="cmts-status-chip">{row.active === false || row.status === "Inactive" ? "Inactive" : String(row.status ?? "Active")}</span>,
};

export const CMTS_ADMIN_RESOURCE_CONFIG: Record<string, CmtsAdminResourceConfig> = {
  masterData: {
    kind: "masterData",
    title: "Master Data Administration",
    subtitle: "Production-grade master data groups and values for complaint lifecycle, workflow, SLA, evidence and reports.",
    hint: "Create, edit, deactivate, reactivate and dependency-check master values. Active values feed forms, filters, workflow actions, dashboards and reports through repository-backed lookup services.",
    createLabel: "Create master value",
    editLabel: "Edit master value",
    rowKey: "id",
    columns: [
      { id: "code", header: "Code", sortable: true, filterable: true, minWidth: 150 },
      { id: "label", header: "Label", sortable: true, filterable: true, minWidth: 220 },
      { id: "group", header: "Group", sortable: true, filterable: true, minWidth: 180 },
      { id: "usageCount", header: "Usage", sortable: true, filterable: true, minWidth: 90 },
      { id: "description", header: "Description", sortable: true, filterable: true, minWidth: 240 },
      activeColumn,
    ],
    filters: [
      { id: "group", label: "Group", columnId: "group", operator: "contains" },
      { id: "status", label: "Status", columnId: "status", operator: "contains" },
    ],
    fields: [
      { name: "code", label: "Code", required: true, minLength: 2 },
      { name: "label", label: "Label", required: true, minLength: 3 },
      { name: "group", label: "Group", type: "select", required: true, options: ["complaint-source","complaint-type","complaint-category","complaint-subcategory","defect-mode","failure-mode","process-area","product-family","severity","priority","risk-level","confidentiality","workflow-status","workflow-stage","rca-status","capa-status","verification-status","containment-status","sla-status","escalation-status","evidence-status","task-status","notification-type","message-type","calendar-event-type","report-type"].map((value) => ({ value, label: value.replaceAll("-", " ") })) },
      { name: "description", label: "Description", type: "textarea" },
    ],
    defaultValues: { group: "complaint-category", active: true, status: "Active" },
    emptyMessage: "No master data values are available.",
    crossDomainUsage: ["Complaint Create dropdowns", "Complaint Register filters", "Workflow status/action lists", "RCA/CAPA/Verification statuses", "Evidence and report filters"],
    dependencyTargets: ["active complaints", "workflow history", "routing rules", "field schemas", "reports"],
    duplicateKeyFields: ["tenantId", "group", "code"],
  },
  referenceData: {
    kind: "referenceData",
    title: "Reference Data Administration",
    subtitle: "Customers, suppliers, products, processes, machines, defect modes and domain reference values.",
    hint: "Reference values are not static fixtures. They drive complaint classification, duplicate/repeat matching, routing, SLA, escalation and report filters.",
    createLabel: "Create reference value",
    editLabel: "Edit reference value",
    rowKey: "id",
    columns: [
      { id: "code", header: "Code", sortable: true, filterable: true, minWidth: 140 },
      { id: "label", header: "Name", sortable: true, filterable: true, minWidth: 220 },
      { id: "group", header: "Type", sortable: true, filterable: true, minWidth: 170 },
      { id: "dependencyStatus", header: "Dependency", sortable: true, filterable: true, minWidth: 160 },
      { id: "description", header: "Description", sortable: true, filterable: true, minWidth: 240 },
      activeColumn,
    ],
    filters: [
      { id: "group", label: "Reference Type", columnId: "group", operator: "contains" },
      { id: "status", label: "Status", columnId: "status", operator: "contains" },
    ],
    fields: [
      { name: "code", label: "Reference Code", required: true, minLength: 2 },
      { name: "label", label: "Reference Name", required: true, minLength: 3 },
      { name: "group", label: "Reference Type", type: "select", required: true, options: ["customers","suppliers","products","parts","processes","lines","machines","departments","sites","plants","product-families","defect-modes","failure-modes","root-cause-categories","containment-types","capa-action-types","verification-methods","evidence-types","sla-policies","escalation-policies"].map((value) => ({ value, label: value.replaceAll("-", " ") })) },
      { name: "description", label: "Description", type: "textarea" },
    ],
    defaultValues: { group: "defect-modes", active: true, status: "Active" },
    emptyMessage: "No reference data values are available.",
    crossDomainUsage: ["Complaint forms", "Routing conditions", "SLA and escalation rules", "CAPA ownership", "Reports and dashboards"],
    dependencyTargets: ["complaints", "routing rules", "field schemas", "reports"],
    duplicateKeyFields: ["tenantId", "group", "code"],
  },
  organization: {
    kind: "organization",
    title: "Organization Administration",
    subtitle: "Business units, sites, departments, users and data-scope assignments.",
    hint: "Organization records govern assignment, department dashboards, task routing, notification recipients and customer/supplier data isolation.",
    createLabel: "Create organization record",
    editLabel: "Edit organization record",
    rowKey: "id",
    columns: [
      { id: "code", header: "Code", sortable: true, filterable: true, minWidth: 140 },
      { id: "name", header: "Name", sortable: true, filterable: true, minWidth: 220 },
      { id: "level", header: "Level", sortable: true, filterable: true, minWidth: 140 },
      { id: "parentCode", header: "Parent", sortable: true, filterable: true, minWidth: 140 },
      { id: "parentId", header: "Parent ID", sortable: true, filterable: true, minWidth: 140 },
      { id: "usageCount", header: "Usage", sortable: true, filterable: true, minWidth: 90 },
      activeColumn,
    ],
    filters: [{ id: "level", label: "Level", columnId: "level", operator: "contains" }, { id: "status", label: "Status", columnId: "status", operator: "contains" }],
    fields: [
      { name: "code", label: "Organization Code", required: true },
      { name: "name", label: "Name", required: true },
      { name: "level", label: "Level", type: "select", required: true, options: [{ value: "Business Unit", label: "Business Unit" }, { value: "Site", label: "Site / Plant" }, { value: "Department", label: "Department" }, { value: "User Scope", label: "User Scope" }, { value: "Customer Scope", label: "Customer Scope" }, { value: "Supplier Scope", label: "Supplier Scope" }] },
      { name: "parentCode", label: "Parent Code" },
      { name: "parentId", label: "Parent ID" },
      { name: "ownerUserId", label: "Default Owner" },
      { name: "description", label: "Description", type: "textarea" },
    ],
    defaultValues: { level: "Department", active: true, status: "Active" },
    emptyMessage: "No organization records are available.",
    crossDomainUsage: ["Complaint assignment", "Dashboard department scope", "Task assignment", "Notification recipients", "External user isolation"],
    dependencyTargets: ["active complaints", "open tasks", "user scopes", "routing rules"],
    duplicateKeyFields: ["tenantId", "level", "code"],
  },
  fieldSchemas: {
    kind: "fieldSchemas",
    title: "Field Schema Administration",
    subtitle: "Dynamic fields, required-field rules and visibility controls for complaint and workflow forms.",
    hint: "Field schemas drive create/edit complaint forms, workflow action payloads and external customer/supplier visibility rules.",
    createLabel: "Create field schema",
    editLabel: "Edit field schema",
    rowKey: "id",
    columns: [
      { id: "code", header: "Code", sortable: true, filterable: true, minWidth: 150 },
      { id: "schema", header: "Schema", sortable: true, filterable: true, minWidth: 220 },
      { id: "schemaName", header: "Schema Name", sortable: true, filterable: true, minWidth: 220 },
      { id: "entityType", header: "Entity Type", sortable: true, filterable: true, minWidth: 160 },
      { id: "version", header: "Version", sortable: true, filterable: true, minWidth: 100 },
      { id: "sourceType", header: "Source", sortable: true, filterable: true, minWidth: 120 },
      { id: "requiredFields", header: "Required Fields", sortable: true, filterable: true, minWidth: 220 },
      { id: "visibility", header: "Visibility", sortable: true, filterable: true, minWidth: 150 },
      { id: "description", header: "Description", sortable: true, filterable: true, minWidth: 220 },
      { id: "schemaDefinition", header: "Schema Definition", sortable: true, filterable: true, minWidth: 260 },
      { id: "validationRules", header: "Validation Rules", sortable: true, filterable: true, minWidth: 220 },
      activeColumn,
    ],
    filters: [{ id: "sourceType", label: "Source", columnId: "sourceType", operator: "contains" }, { id: "entityType", label: "Entity Type", columnId: "entityType", operator: "contains" }, { id: "visibility", label: "Visibility", columnId: "visibility", operator: "contains" }],
    fields: [
      { name: "code", label: "Schema Code", required: true },
      { name: "schema", label: "Schema Name", required: true },
      { name: "schemaName", label: "Display Name", required: true },
      { name: "entityType", label: "Entity Type", type: "select", required: true, options: [{ value: "complaint", label: "Complaint" }, { value: "rca", label: "RCA" }, { value: "capa", label: "CAPA" }, { value: "verification", label: "Verification" }, { value: "evidence", label: "Evidence" }, { value: "workflow", label: "Workflow" }] },
      { name: "version", label: "Version", type: "number", required: true },
      { name: "sourceType", label: "Source Type", type: "select", required: true, options: [{ value: "internal", label: "Internal" }, { value: "customer", label: "Customer" }, { value: "supplier", label: "Supplier" }, { value: "safety", label: "Safety" }] },
      { name: "category", label: "Category" },
      { name: "requiredFields", label: "Required Fields", required: true, help: "Comma-separated field codes." },
      { name: "visibility", label: "Visibility", type: "select", required: true, options: [{ value: "internal", label: "Internal" }, { value: "customer-visible", label: "Customer visible" }, { value: "supplier-visible", label: "Supplier visible" }] },
      { name: "description", label: "Description", type: "textarea" },
      { name: "schemaDefinition", label: "Schema Definition JSON", type: "textarea", required: true, help: "JSON object/array defining fields, validation and UI metadata." },
      { name: "validationRules", label: "Validation Rules", type: "textarea" },
    ],
    defaultValues: { sourceType: "customer", visibility: "customer-visible", entityType: "complaint", version: 1, schemaDefinition: "{}", active: true, status: "Active" },
    emptyMessage: "No field schemas are available.",
    crossDomainUsage: ["Complaint create/edit forms", "Workflow action modals", "Evidence metadata", "External response forms"],
    dependencyTargets: ["complaint type", "category", "external response forms", "evidence forms"],
    duplicateKeyFields: ["tenantId", "code"],
  },
  routing: {
    kind: "routing",
    title: "Routing Administration",
    subtitle: "Assignment, workflow profile, SLA, escalation and evidence policy routing rules.",
    hint: "Routing rules must be testable and must drive owner assignment, workflow profile, SLA policy, escalation and evidence policy selection.",
    createLabel: "Create routing rule",
    editLabel: "Edit routing rule",
    testLabel: "Test routing",
    rowKey: "id",
    columns: [
      { id: "code", header: "Rule Code", sortable: true, filterable: true, minWidth: 150 },
      { id: "ruleName", header: "Rule Name", sortable: true, filterable: true, minWidth: 220 },
      { id: "sourceType", header: "Source", sortable: true, filterable: true, minWidth: 120 },
      { id: "category", header: "Category", sortable: true, filterable: true, minWidth: 130 },
      { id: "priority", header: "Priority", sortable: true, filterable: true, minWidth: 110 },
      { id: "condition", header: "Condition", sortable: true, filterable: true, minWidth: 260 },
      { id: "targetQueue", header: "Target Queue", sortable: true, filterable: true, minWidth: 160 },
      { id: "departmentId", header: "Department", sortable: true, filterable: true, minWidth: 150 },
      { id: "ownerId", header: "Owner", sortable: true, filterable: true, minWidth: 170 },
      { id: "slaPolicy", header: "SLA Policy", sortable: true, filterable: true, minWidth: 140 },
      { id: "escalationPolicy", header: "Escalation", sortable: true, filterable: true, minWidth: 140 },
      { id: "workflowProfile", header: "Workflow", sortable: true, filterable: true, minWidth: 160 },
      activeColumn,
    ],
    filters: [{ id: "priority", label: "Priority", columnId: "priority", operator: "contains" }, { id: "category", label: "Category", columnId: "category", operator: "contains" }, { id: "targetQueue", label: "Target Queue", columnId: "targetQueue", operator: "contains" }, { id: "status", label: "Status", columnId: "status", operator: "contains" }],
    fields: [
      { name: "code", label: "Routing Rule Code", required: true },
      { name: "ruleName", label: "Routing Rule Name", required: true },
      { name: "sourceType", label: "Source Type", type: "select", required: true, options: [{ value: "internal", label: "Internal" }, { value: "customer", label: "Customer" }, { value: "supplier", label: "Supplier" }, { value: "safety", label: "Safety" }] },
      { name: "category", label: "Category", required: true },
      { name: "priority", label: "Priority", type: "select", required: true, options: [{ value: "low", label: "Low" }, { value: "medium", label: "Medium" }, { value: "high", label: "High" }, { value: "critical", label: "Critical" }] },
      { name: "condition", label: "Routing Condition", type: "textarea", required: true, help: "Human-readable rule condition or expression used during routing evaluation." },
      { name: "targetQueue", label: "Target Queue", required: true },
      { name: "departmentId", label: "Target Department" },
      { name: "ownerId", label: "Target Owner" },
      { name: "slaPolicy", label: "SLA Policy", required: true },
      { name: "escalationPolicy", label: "Escalation Policy" },
      { name: "workflowProfile", label: "Workflow Profile", required: true },
    ],
    defaultValues: { priority: "high", sourceType: "customer", targetQueue: "quality-triage", active: true, status: "Active" },
    emptyMessage: "No routing rules are configured.",
    crossDomainUsage: ["Complaint create submit", "Assignment", "Workflow profile selection", "SLA policy", "Escalation policy", "Notification recipient/template selection"],
    dependencyTargets: ["active complaints", "workflow history", "SLA states", "escalations"],
    duplicateKeyFields: ["tenantId", "code"],
  },
  sod: {
    kind: "sod",
    title: "SoD Policy Administration",
    subtitle: "Segregation-of-duties controls for workflow, evidence, closure and configuration governance.",
    hint: "SoD policies must be testable and govern action decisions through the same RBAC/SoD engine used by workflow buttons.",
    createLabel: "Create SoD policy",
    editLabel: "Edit SoD policy",
    testLabel: "Test SoD policy",
    rowKey: "id",
    columns: [
      { id: "code", header: "Code", sortable: true, filterable: true },
      { id: "policy", header: "Policy", sortable: true, filterable: true, minWidth: 240 },
      { id: "appliesTo", header: "Applies To", sortable: true, filterable: true },
      { id: "result", header: "Result", sortable: true, filterable: true },
      activeColumn,
    ],
    filters: [{ id: "appliesTo", label: "Applies To", columnId: "appliesTo", operator: "contains" }, { id: "result", label: "Result", columnId: "result", operator: "contains" }],
    fields: [
      { name: "code", label: "Policy Code", required: true },
      { name: "policy", label: "Policy", required: true },
      { name: "appliesTo", label: "Applies To", type: "select", required: true, options: [{ value: "approval", label: "Approval" }, { value: "closure", label: "Closure" }, { value: "verification", label: "Verification" }, { value: "configuration", label: "Configuration" }] },
      { name: "result", label: "Result", type: "select", required: true, options: [{ value: "blocked", label: "Blocked" }, { value: "warn", label: "Warn" }] },
      { name: "reason", label: "Reason", type: "textarea" },
    ],
    defaultValues: { appliesTo: "closure", result: "blocked", active: true, status: "Active" },
    emptyMessage: "No SoD policies are configured.",
    crossDomainUsage: ["Workflow allowed actions", "RCA approval", "CAPA verification", "Closure approval", "Configuration governance"],
    dependencyTargets: ["workflow actions", "roles", "permissions", "configuration drafts"],
    duplicateKeyFields: ["tenantId", "code"],
  },
  evidencePolicies: {
    kind: "evidencePolicies",
    title: "Evidence Policy Administration",
    subtitle: "Mandatory evidence, allowed file types, confidentiality, retention and package export rules.",
    hint: "Evidence policies drive complaint create, workflow blockers, upload/download visibility and closure package export.",
    createLabel: "Create evidence policy",
    editLabel: "Edit evidence policy",
    testLabel: "Test policy",
    rowKey: "id",
    columns: [
      { id: "code", header: "Policy", sortable: true, filterable: true, minWidth: 150 },
      { id: "label", header: "Name", sortable: true, filterable: true, minWidth: 220 },
      { id: "appliesTo", header: "Applies To", sortable: true, filterable: true, minWidth: 170 },
      { id: "mandatoryStages", header: "Mandatory Stages", sortable: true, filterable: true, minWidth: 220 },
      { id: "maxFileSizeMb", header: "Max MB", sortable: true, filterable: true, minWidth: 90 },
      activeColumn,
    ],
    filters: [{ id: "appliesTo", label: "Applies To", columnId: "appliesTo", operator: "contains" }, { id: "status", label: "Status", columnId: "status", operator: "contains" }],
    fields: [
      { name: "code", label: "Policy Code", required: true },
      { name: "label", label: "Policy Name", required: true },
      { name: "appliesTo", label: "Applies To", required: true },
      { name: "requiredEvidenceTypes", label: "Required Evidence Types", required: true },
      { name: "allowedFileTypes", label: "Allowed File Types", required: true },
      { name: "maxFileSizeMb", label: "Max File Size MB", type: "number", required: true },
      { name: "mandatoryStages", label: "Mandatory Stages", required: true },
    ],
    defaultValues: { appliesTo: "critical customer complaints", requiredEvidenceTypes: "photo,inspection-report", allowedFileTypes: "pdf,jpg,png,xlsx", maxFileSizeMb: 20, mandatoryStages: "Create,Closure", active: true, status: "Active" },
    emptyMessage: "No evidence policies are configured.",
    crossDomainUsage: ["Complaint Create", "Workflow evidence requirement", "Closure blockers", "Evidence package export"],
    dependencyTargets: ["complaints", "evidence items", "workflow actions", "closure records"],
    duplicateKeyFields: ["tenantId", "code"],
  },
  notificationTemplates: {
    kind: "notificationTemplates",
    title: "Notification Template Administration",
    subtitle: "Templates, trigger events, channels, recipients and retry policies.",
    hint: "Notification templates are consumed by the operation spine for workflow, SLA, escalation and customer/supplier events.",
    createLabel: "Create notification template",
    editLabel: "Edit notification template",
    previewLabel: "Preview template",
    rowKey: "id",
    columns: [
      { id: "code", header: "Template", sortable: true, filterable: true, minWidth: 150 },
      { id: "label", header: "Name", sortable: true, filterable: true, minWidth: 220 },
      { id: "triggerEvent", header: "Trigger", sortable: true, filterable: true, minWidth: 170 },
      { id: "channel", header: "Channel", sortable: true, filterable: true, minWidth: 120 },
      { id: "recipientRule", header: "Recipient Rule", sortable: true, filterable: true, minWidth: 170 },
      activeColumn,
    ],
    filters: [{ id: "triggerEvent", label: "Trigger", columnId: "triggerEvent", operator: "contains" }, { id: "channel", label: "Channel", columnId: "channel", operator: "contains" }],
    fields: [
      { name: "code", label: "Template Code", required: true },
      { name: "label", label: "Template Name", required: true },
      { name: "triggerEvent", label: "Trigger Event", required: true },
      { name: "channel", label: "Channel", type: "select", required: true, options: [{ value: "in_app", label: "In-app" }, { value: "email", label: "Email" }, { value: "sms", label: "SMS" }, { value: "whatsapp", label: "WhatsApp" }, { value: "webhook", label: "Webhook" }] },
      { name: "recipientRule", label: "Recipient Rule", required: true },
      { name: "subject", label: "Subject", required: true },
      { name: "body", label: "Body", type: "textarea", required: true },
    ],
    defaultValues: { channel: "in_app", triggerEvent: "WORKFLOW_ACTION_EXECUTED", recipientRule: "owner-and-manager", active: true, status: "Active" },
    emptyMessage: "No notification templates are configured.",
    crossDomainUsage: ["Workflow action notifications", "SLA breach notifications", "Escalation notifications", "RCA/CAPA/Verification notifications"],
    dependencyTargets: ["operation spine", "notification dispatch attempts", "scheduled jobs"],
    duplicateKeyFields: ["tenantId", "code"],
  },
  calendarRules: {
    kind: "calendarRules",
    title: "Calendar and Scheduler Administration",
    subtitle: "Working-day calendars, holiday rules, reminder rules and scheduled jobs.",
    hint: "Calendar rules drive SLA due-date calculation, reminders, near-breach detection and scheduled operation-spine jobs.",
    createLabel: "Create calendar rule",
    editLabel: "Edit calendar rule",
    testLabel: "Manual run / test",
    rowKey: "id",
    columns: [
      { id: "code", header: "Rule", sortable: true, filterable: true, minWidth: 150 },
      { id: "label", header: "Name", sortable: true, filterable: true, minWidth: 220 },
      { id: "ruleType", header: "Type", sortable: true, filterable: true, minWidth: 130 },
      { id: "frequency", header: "Frequency", sortable: true, filterable: true, minWidth: 130 },
      { id: "nextRun", header: "Next Run", sortable: true, filterable: true, minWidth: 160 },
      activeColumn,
    ],
    filters: [{ id: "ruleType", label: "Rule Type", columnId: "ruleType", operator: "contains" }, { id: "status", label: "Status", columnId: "status", operator: "contains" }],
    fields: [
      { name: "code", label: "Rule Code", required: true },
      { name: "label", label: "Rule Name", required: true },
      { name: "ruleType", label: "Rule Type", type: "select", required: true, options: [{ value: "working-day", label: "Working Day" }, { value: "holiday", label: "Holiday" }, { value: "reminder", label: "Reminder" }, { value: "scheduled-job", label: "Scheduled Job" }] },
      { name: "frequency", label: "Frequency" },
      { name: "nextRun", label: "Next Run", type: "datetime-local" },
      { name: "description", label: "Description", type: "textarea" },
    ],
    defaultValues: { ruleType: "reminder", frequency: "daily", active: true, status: "Active" },
    emptyMessage: "No calendar or scheduler rules are configured.",
    crossDomainUsage: ["SLA due-date calculation", "Reminder creation", "Escalation near-breach detection", "Scheduled job manual run"],
    dependencyTargets: ["SLA states", "calendar events", "scheduled jobs", "reminders"],
    duplicateKeyFields: ["tenantId", "code"],
  },
  scheduledJobs: {
    kind: "scheduledJobs",
    title: "Scheduled Job Administration",
    subtitle: "Runtime scheduled jobs, reminder evaluation and retry governance.",
    hint: "Scheduled jobs evaluate SLA breaches, reminders, export cleanup and operation-spine retries.",
    createLabel: "Create scheduled job",
    editLabel: "Edit scheduled job",
    testLabel: "Run now",
    rowKey: "id",
    columns: [
      { id: "jobCode", header: "Job Code", sortable: true, filterable: true, minWidth: 150 },
      { id: "jobName", header: "Job", sortable: true, filterable: true, minWidth: 220 },
      { id: "jobType", header: "Job Type", sortable: true, filterable: true, minWidth: 130 },
      { id: "cronExpression", header: "Cron Expression", sortable: true, filterable: true, minWidth: 160 },
      { id: "maxRetryCount", header: "Max Retries", sortable: true, minWidth: 120 },
      { id: "timeoutSeconds", header: "Timeout (seconds)", sortable: true, minWidth: 140 },
      activeColumn,
    ],
    filters: [{ id: "jobType", label: "Job Type", columnId: "jobType", operator: "contains" }, { id: "status", label: "Status", columnId: "status", operator: "contains" }],
    fields: [
      { name: "jobCode", label: "Job Code", required: true },
      { name: "jobName", label: "Job Name", required: true },
      { name: "jobType", label: "Job Type", type: "select", required: true, options: [{ value: "SLA_EVALUATION", label: "SLA Evaluation" }, { value: "REMINDER_DISPATCH", label: "Reminder Dispatch" }, { value: "EXPORT_CLEANUP", label: "Export Cleanup" }, { value: "OPERATION_RETRY", label: "Operation Retry" }] },
      { name: "cronExpression", label: "Cron Expression", required: true, help: "Five-field cron expression, e.g. 0 * * * *." },
      { name: "maxRetryCount", label: "Maximum Retry Count", type: "number", required: true },
      { name: "timeoutSeconds", label: "Timeout Seconds", type: "number", required: true },
      { name: "isActive", label: "Active", type: "select", required: true, options: [{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }] },
    ],
    defaultValues: { jobType: "SLA_EVALUATION", cronExpression: "0 * * * *", maxRetryCount: 3, timeoutSeconds: 600, isActive: true, active: true, status: "Active" },
    emptyMessage: "No scheduled jobs are configured.",
    crossDomainUsage: ["SLA breach evaluation", "Reminder dispatch", "Report export cleanup", "Operation-spine retries"],
    dependencyTargets: ["calendar rules", "SLA states", "notifications", "audit events"],
    duplicateKeyFields: ["tenantId", "jobCode"],
  },
};

export function adminConfigFor(kind: string): CmtsAdminResourceConfig {
  return CMTS_ADMIN_RESOURCE_CONFIG[kind] ?? CMTS_ADMIN_RESOURCE_CONFIG.masterData!;
}
